import _, { get, isString, isEmpty } from "lodash";
import { BILLING_CYCLE, BillingCycleEnum, planTypes } from '@/common/constants';
import { mapGetters, mapMutations } from 'vuex';
import { flowTypes } from '@/common/service/page-config';
import { isAutoAddable, isShowRemoveBtn } from '@/plans/ZOOM_MEETING/common/utils';
import { plansof } from '@/utils/chain';
import { PBX_SUB_TYPES } from "@/common/service/computers/plans/pbx";
import { deduplicate } from "@/common/utils";
import softBundlePlansMixin from '@/views/config/soft-bundle/mixin/soft-bundle-plan';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

export default {
  mixins: [softBundlePlansMixin],
  props: {
    configPlanTileStatus: {
      type: Object,
      default: () => ({}),
    },
    isAddon: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      isExpanded: false,
      planCardState: {
        removeLoading: false,
      }
    }
  },
  computed: {
    ...mapGetters(['currentEditPlan', 'basicConfig', 'planMapFlat', 'selectedSingleUsePro', 'getSelectedPlans', 'isProFreeTrialAddAddonCase', 'supportAutoAddAnotherBasePlan', 'preventAutoCollapseOnce']),
    isEditFlow() {
      return this.$context.flow === flowTypes.edit && this.$context.is.editPage;
    },
    planList() {
      return plansof(this.planMapFlat).type(this.planType).get();
    },
    isAdd2CartOrEditFlow() {
      return this.$context.is.addPage || this.$context.is.editPage;
    },
    isShowRemoveBtn() {
      const selectedTypes = deduplicate(
        this.getSelectedPlans
        .filter(item => !(item.type === planTypes.ZOOM_PHONE && item.sub_type === PBX_SUB_TYPES.BASE))
        .map(item => item.type)
      );
      const isSelected = selectedTypes.includes(this.planType);
      return isShowRemoveBtn(this.planType) && isSelected && selectedTypes?.length > 1;
    },
    hasAccountDiscount() {
      return get(this.basicConfig, 'hasAccountDiscount', false);
    },
    isStandardPrice() {
      const isEditFlow = this.$context.flow === flowTypes.edit;
      return (isEditFlow && !this.hasAccountDiscount) ? this.currentEditPlan?.isStandardPrice : true;
    },
    forNonStandardPriceTips() {
      return !this.isStandardPrice
        ? this.$t('billing.planform.meeting_license_custom_pricing')
        : '';
    },
    monthlyPlan() {
      const monthlyPlan = plansof(this.planList).monthly().get(0);
      if (isEmpty(monthlyPlan)) return null;

      const depAddonMaxCyclePlan = this.depAddonMaxCyclePlan;
      const basicItem = { plan: monthlyPlan, disabled: false, disabledTooltip: '' };

      // check for depend plan list with billing cycle
      if (depAddonMaxCyclePlan?.billing_cycle === BILLING_CYCLE.ANNUAL) {
        const addonPlanName = this.$t(depAddonMaxCyclePlan?.typeNameKey);
        const tooltips = this.$t('billing.planform.monthly_as_base_disable_tooltip', [addonPlanName, this.title, addonPlanName]);
        const alert = this.$t('billing.planform.can_not_select_monthly_with_annual_addon', [addonPlanName, this.title]);

        return {
          ...basicItem,
          tooltips,
          alert,
          disabled: true,
        };
      }

      // check for bulk license
      if (this.disabledForBulkLicense && this.disabledForBulkLicense(BILLING_CYCLE.MONTHLY)) {
        return {
          ...basicItem,
          disabled: true,
          tooltips: this.disabledForBulkLicenseTips,
          alert: this.disabledForBulkLicenseTips,
        };
      }

      if (this.soft_bundle_disableBillingCycleSwitch) {
        const enableBillingCycle = this.soft_bundle_enableBillingCycle;
        if (enableBillingCycle && enableBillingCycle !== BILLING_CYCLE.MONTHLY) {
          return {
            ...basicItem,
            disabled: true,
            disabledTooltip: this.$t('softBundle.disableCycleTips', [enableBillingCycle]),
          };
        }
      }

      return basicItem;
    },
    annualPlan() {
      const annualPlan = plansof(this.planList).annual().get(0);
      if (isEmpty(annualPlan)) return null;

      const depFirstPlan = this.currentPlanCycleDepFirstPlan;

      const basicItem = {plan: annualPlan, disabled: false, disabledTooltip: ''};

      // check for depend plan list with billing cycle
      if (depFirstPlan?.cycleValue < BillingCycleEnum[BILLING_CYCLE.ANNUAL] && !this.supportAutoAddAnotherBasePlan) {
        const basePlanName = this.$t(depFirstPlan?.typeNameKey);
        const alert = this.$t('billing.planform.can_not_select_annual_with_base', [basePlanName]);
        const tooltips = this.$t('billing.planform.annual_addon_disable_tooltip', [basePlanName, this.title, basePlanName]);

        return {
          ...basicItem,
          alert,
          tooltips,
          disabled: true,
        };
      }
      // check for bulk license
      if (this.disabledForBulkLicense && this.disabledForBulkLicense(BILLING_CYCLE.ANNUAL)) {
        return {
          ...basicItem,
          disabled: true,
          tooltips: this.disabledForBulkLicenseTips,
          alert: this.disabledForBulkLicenseTips,
        };
      }

      if (this.soft_bundle_disableBillingCycleSwitch) {
        const enableBillingCycle = this.soft_bundle_enableBillingCycle;

        if (enableBillingCycle && enableBillingCycle !== BILLING_CYCLE.ANNUAL) {
          return {
            ...basicItem,
            disabled: true,
            disabledTooltip: this.$t('softBundle.disableCycleTips', [enableBillingCycle]),
          };
        }
      }

      return basicItem;
    },
    quarterlyPlan() {
      const quarterlyPlan = plansof(this.planList).quarterly().get(0);
      if (isEmpty(quarterlyPlan)) return null;

      return { plan: quarterlyPlan, disabled: false, disabledTooltip: '' };
    },
    twoYearsPlan() {
      const twoYearsPlan = plansof(this.planList).twoYears().get(0);
      if (isEmpty(twoYearsPlan)) return null;

      return { plan: twoYearsPlan, disabled: false, disabledTooltip: '' };
    },
    threeYearsPlan() {
      const threeYearsPlan = plansof(this.planList).threeYears().get(0);
      if (isEmpty(threeYearsPlan)) return null;

      return { plan: threeYearsPlan, disabled: false, disabledTooltip: '' };
    },
    billingCycleList() {
      return _.chain([this.monthlyPlan, this.quarterlyPlan, this.annualPlan, this.twoYearsPlan, this.threeYearsPlan])
      .compact()
      .reverse()
      .value();
    },
    hasOneTimeWebinar() {
      return this.getSelectedPlans.find(p => p.billing_cycle === BILLING_CYCLE.ONE_TIME && p.type === planTypes.WEBINAR);
    }
  },
  mounted () {
    this.autoFocus();
  },
  methods: {
    ...mapMutations(['setPreventAutoCollapseOnce']),
    startRemoveLoading() {
      this.toggleRemoveLoading(true);
    },
    stopRemoveLoading() {
      this.toggleRemoveLoading(false);
    },
    toggleRemoveLoading(value) {
      this.planCardState.removeLoading = value;
    },
    addDraftPlanByKey(key) {
      // Add an empty plan to the panel
      this.$emit('add', key)
    },
    async removePlanByKey(key) {
      try {
        this.startRemoveLoading();
        await this.validateSoftBundlePlanWhenRemove(this.stopRemoveLoading);
        // Unified key retrieval: use $options.key (defined in mixin or via extends) as the single source of truth
        // Note: ADDITIONAL_HOST and ADDITIONAL_CO_EDITOR use extends to set key on Basic component
        // Vue 3: $vnode?.key is no longer available
        // All plan components must define key in their mixin or via extends
        const planKey = isString(key) ? key : this.$options?.key;
        if (!planKey) {
          console.error('Plan key is not defined. Component must define key in mixin or via extends.');
        }
        this.$emit('remove', planKey);
        // for event tracking
        const buyInteraction = this.isAddon ? this.$tracking.addOnBuyInteraction : this.$tracking.buyInteraction;
        buyInteraction(`${getTrackingProductNameByPlanType(planKey)} options`, "remove");
      } finally {
        this.stopRemoveLoading();
      }
    },
    getFirstFocusableControl() {
      return new Promise(resolve => {
        this.$nextTick(() => {
          const inputs = this.$el.querySelectorAll('.opc-foldable input');
          if (inputs) {
            resolve(inputs[0])
          } else {
            resolve()
          }
        })
      })
    },
    async autoFocus () {
      if (document.addPlanFromEnterKey) {
        let focusEl = this.getFirstFocusableControl ? await this.getFirstFocusableControl() : null;
        if (focusEl) {
          focusEl.focus();
          document.addPlanFromEnterKey = false;
        }
      }
    },
  },
  watch: {
    configPlanTileStatus: {
      deep: true,
      handler(configPlanTileStatus) {
        // * fix when in edit or pro2biz flow, the plan card auto collapse
        if (this.$context.is.edit || this.$context.is.pro2biz || this.$context.is.biz2pro || this.$context.is.edu2esc) return
        if (this.hasOneTimeWebinar && this.$context.is.add2cart) return
        if (this.selectedSingleUsePro) return
        if (isAutoAddable(this.planType)) return
        // Unified key retrieval: use $options.key (defined in mixin or via extends) as the single source of truth
        // Note: ADDITIONAL_HOST and ADDITIONAL_CO_EDITOR use extends to set key on Basic component
        // Vue 3: $vnode?.key is no longer available
        // All plan components must define key in their mixin or via extends
        const planKey = this.$options?.key;
        if (!planKey) {
          console.error('Plan key is not defined. Component must define key in mixin or via extends.');
        }
        const isExpanded = get(configPlanTileStatus, planKey, false) ? false : true;
        if (this.preventAutoCollapseOnce && !isExpanded) {
          this.$nextTick(() => this.setPreventAutoCollapseOnce(false));
          return
        }
        if (this.isProFreeTrialAddAddonCase) return;
        console.log('debugger: watch: configPlanTileStatus', isExpanded);
        this.isExpanded = isExpanded;
        this.$emit('expand-change', isExpanded);
      },
    }
  }
}
